import React from 'react';
import { Link, useLocation, useNavigate, useParams } from 'react-router-dom';
import {
  Alert,
  Box,
  Button,
  Card,
  CardContent,
  Chip,
  CircularProgress,
  Container,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  Divider,
  Grid,
  Paper,
  Stack,
  TextField,
  Typography,
} from '@mui/material';
import { DatePicker, LocalizationProvider } from '@mui/x-date-pickers';
import { AdapterDayjs } from '@mui/x-date-pickers/AdapterDayjs';
import dayjs, { type Dayjs } from 'dayjs';
import { useTranslation } from 'react-i18next';

import SEO from '../../components/SEO';
import { gearApiClient } from '../../gearApiClient';
import { authService } from '../../services/AuthService';
import { gearIcons, getGearLabel } from '../../gears';
import type { GearItem } from '../../models/GearItem';
import Inventory2Icon from '@mui/icons-material/Inventory2';

type GearItemViewModel = GearItem & {
  inventoryId?: string;
  inventoryName?: string;
  organizationName?: string;
  addressLine1?: string;
  addressLine2?: string | null;
  city?: string;
  zipCode?: string | null;
  countryCode?: string;
  returnByTime?: string;
};

function normalizeGearItem(item: GearItemViewModel): GearItemViewModel {
  return {
    ...item,
    inventory: item.inventory ?? {
      id: item.inventoryId ?? '',
      organizationName: item.organizationName ?? '',
      name: item.inventoryName ?? '',
      addressLine1: item.addressLine1 ?? '',
      addressLine2: item.addressLine2 ?? null,
      city: item.city ?? '',
      zipCode: item.zipCode ?? null,
      countryCode: item.countryCode ?? '',
      returnByTime: item.returnByTime,
    },
  };
}

const DATE_FORMAT = 'YYYY-MM-DD';

function parseQueryDate(value: string | null): Dayjs | null {
  if (!value) {
    return null;
  }
  const parsed = dayjs(value);
  return parsed.isValid() ? parsed : null;
}

function formatDate(date: Dayjs | null): string | undefined {
  return date && date.isValid() ? date.format(DATE_FORMAT) : undefined;
}

function formatCurrency(amount: number, currencyCode: string): string {
  return new Intl.NumberFormat(undefined, {
    style: 'currency',
    currency: currencyCode,
    maximumFractionDigits: 2,
  }).format(amount);
}

function formatInventoryAddress(item: GearItemViewModel): string {
  const inventory = item.inventory;
  const pieces = [inventory?.addressLine1, inventory?.addressLine2, inventory?.city, inventory?.countryCode]
    .filter(Boolean)
    .map((value) => String(value).trim());
  return pieces.join(', ');
}

export default function GearItemDetailPage() {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const location = useLocation();
  const { itemId } = useParams<{ itemId: string }>();

  const [item, setItem] = React.useState<GearItemViewModel | null>(null);
  const [loading, setLoading] = React.useState(true);
  const [error, setError] = React.useState('');
  const [startDate, setStartDate] = React.useState<Dayjs | null>(null);
  const [endDate, setEndDate] = React.useState<Dayjs | null>(null);
  const [quantity, setQuantity] = React.useState(1);
  const [availabilityLoading, setAvailabilityLoading] = React.useState(false);
  const [availability, setAvailability] = React.useState<boolean | null>(null);
  const [availabilityError, setAvailabilityError] = React.useState('');
  const [confirmOpen, setConfirmOpen] = React.useState(false);
  const [submitLoading, setSubmitLoading] = React.useState(false);
  const [reservationError, setReservationError] = React.useState('');
  const [reservationSuccess, setReservationSuccess] = React.useState(false);

  React.useEffect(() => {
    const params = new URLSearchParams(location.search);
    const nextStart = parseQueryDate(params.get('startDate'));
    const nextEnd = parseQueryDate(params.get('endDate'));
    const nextQuantity = Number.parseInt(params.get('quantity') ?? '1', 10);

    setStartDate(nextStart);
    setEndDate(nextEnd);
    setQuantity(Number.isFinite(nextQuantity) && nextQuantity > 0 ? nextQuantity : 1);
  }, [location.search]);

  React.useEffect(() => {
    if (!itemId) {
      setError(t('gearDetail.notFound', 'Gear item not found'));
      setLoading(false);
      return;
    }

    let cancelled = false;

    const loadItem = async () => {
      setLoading(true);
      setError('');

      try {
        const response = await gearApiClient.getGearItem(itemId);
        if (!cancelled) {
          setItem(normalizeGearItem(response));
        }
      } catch (err: any) {
        if (!cancelled) {
          if (err?.response?.status === 404) {
            setError(t('gearDetail.notFound', 'Gear item not found'));
          } else {
            setError(t('gearDetail.loadError', 'Failed to load gear item'));
          }
          setItem(null);
        }
      } finally {
        if (!cancelled) {
          setLoading(false);
        }
      }
    };

    void loadItem();

    return () => {
      cancelled = true;
    };
  }, [itemId, t]);

  React.useEffect(() => {
    if (!itemId) {
      return;
    }

    const start = formatDate(startDate);
    const end = formatDate(endDate);

    if (!start || !end) {
      setAvailability(null);
      setAvailabilityError('');
      return;
    }

    if (!endDate || !startDate || !endDate.isAfter(startDate, 'day')) {
      setAvailability(null);
      setAvailabilityError(t('gearDetail.invalidDateRange', 'End date must be after start date'));
      return;
    }

    let cancelled = false;

    const checkAvailability = async () => {
      setAvailabilityLoading(true);
      setAvailabilityError('');

      try {
        const response = await gearApiClient.getGearItem(itemId, { startDate: start, endDate: end });
        if (!cancelled) {
          setItem(normalizeGearItem(response));
          setAvailability(typeof response.available === 'boolean' ? response.available : null);
        }
      } catch (err: any) {
        if (!cancelled) {
          setAvailability(null);
          setAvailabilityError(t('gearDetail.availabilityLoadError', 'Could not check availability'));
        }
      } finally {
        if (!cancelled) {
          setAvailabilityLoading(false);
        }
      }
    };

    void checkAvailability();

    return () => {
      cancelled = true;
    };
  }, [itemId, startDate, endDate, t]);

  const selectedDateRangeValid = Boolean(startDate && endDate && endDate.isAfter(startDate, 'day'));
  const selectedDateRange = selectedDateRangeValid ? `${formatDate(startDate)}..${formatDate(endDate)}` : '';
  const rentalDays = selectedDateRangeValid && startDate && endDate ? endDate.diff(startDate, 'day') : 0;
  const referenceTotal = item && rentalDays > 0 ? item.pricePerDay * quantity * rentalDays : 0;
  const itemIcon = item ? gearIcons[item.gearType] ?? <Inventory2Icon fontSize="small" /> : <Inventory2Icon fontSize="small" />;
  const returnByTime = item?.inventory?.returnByTime ?? '12:00';
  const canReserve = Boolean(item && selectedDateRangeValid && availability !== false && !availabilityLoading && !submitLoading && rentalDays > 0);

  const buildReturnUrl = React.useCallback(() => {
    const params = new URLSearchParams();
    const formattedStart = formatDate(startDate);
    const formattedEnd = formatDate(endDate);
    if (formattedStart) {
      params.set('startDate', formattedStart);
    }
    if (formattedEnd) {
      params.set('endDate', formattedEnd);
    }
    params.set('quantity', String(quantity));
    const query = params.toString();
    return `${location.pathname}${query ? `?${query}` : ''}`;
  }, [endDate, location.pathname, quantity, startDate]);

  const handleReserve = () => {
    if (!item) {
      return;
    }

    if (!authService.getIsLoggedIn()) {
      navigate('/login', { state: { redirectUrl: buildReturnUrl() } });
      return;
    }

    setReservationError('');
    setReservationSuccess(false);
    setConfirmOpen(true);
  };

  const handleSubmitReservation = async () => {
    if (!item || !startDate || !endDate) {
      return;
    }

    const formattedStart = formatDate(startDate);
    const formattedEnd = formatDate(endDate);
    if (!formattedStart || !formattedEnd) {
      return;
    }

    setSubmitLoading(true);
    setReservationError('');

    try {
      await gearApiClient.createGearRental({
        gearItemId: item.id,
        quantity,
        startDate: formattedStart,
        endDate: formattedEnd,
      });
      setConfirmOpen(false);
      setReservationSuccess(true);
    } catch (err: any) {
      const status = err?.response?.status;
      setConfirmOpen(false);
      setReservationSuccess(false);
      if (status === 409) {
        setReservationError(t('gearDetail.conflictError', 'Not enough units available for the selected dates'));
      } else if (status === 400) {
        setReservationError(t('gearDetail.invalidDateRange', 'End date must be after start date'));
      } else {
        setReservationError(t('gearDetail.submitError', 'Could not create reservation'));
      }
    } finally {
      setSubmitLoading(false);
    }
  };

  if (loading) {
    return (
      <Container maxWidth="md" sx={{ py: 6 }}>
        <Box sx={{ display: 'flex', justifyContent: 'center', py: 8 }}>
          <CircularProgress />
        </Box>
      </Container>
    );
  }

  return (
    <>
      <SEO
        title={item ? `${getGearLabel(item.gearType)} | ${t('gearDetail.seoTitle', 'Rent gear')}` : t('gearDetail.seoTitle', 'Rent gear')}
        description={t('gearDetail.seoDescription', 'Check availability and reserve gear from the public catalog.')}
      />
      <Container maxWidth="md" sx={{ py: { xs: 3, md: 5 } }}>
        <Stack spacing={3}>
          <Box>
            <Button component={Link} to="/rent-gear" variant="text" sx={{ mb: 1, px: 0 }}>
              {t('gearDetail.backToCatalog', 'Back to catalog')}
            </Button>
            <Typography component="h1" variant="h4" fontWeight={800}>
              {item ? getGearLabel(item.gearType) : t('gearDetail.title', 'Gear reservation')}
            </Typography>
            {item ? (
              <Typography color="text.secondary" sx={{ mt: 0.5 }}>
                {item.inventory.organizationName} · {item.inventory.city}
              </Typography>
            ) : null}
          </Box>

          {error ? <Alert severity="error">{error}</Alert> : null}
          {reservationError ? <Alert severity="error">{reservationError}</Alert> : null}
          {reservationSuccess ? (
            <Alert severity="success">
              <Stack spacing={1}>
                <Typography fontWeight={700}>{t('gearDetail.successTitle', 'Reservation pending confirmation')}</Typography>
                <Typography variant="body2">{t('gearDetail.successBody', 'We sent your request to the organization. Pickup address: {{address}}', { address: item ? formatInventoryAddress(item) : '' })}</Typography>
                <Typography variant="body2">{t('gearDetail.returnByNotice', 'Return by {{time}} on the return date.', { time: returnByTime })}</Typography>
                <Box>
                  <Button component={Link} to="/user/gear-rentals" variant="contained" size="small">
                    {t('gearDetail.goToRentals', 'My Gear Rentals')}
                  </Button>
                </Box>
              </Stack>
            </Alert>
          ) : null}

          {item ? (
            <Grid container spacing={3}>
              <Grid item xs={12} md={7}>
                <Card variant="outlined" sx={{ borderRadius: 3 }}>
                  <CardContent>
                    <Stack spacing={2}>
                      <Stack direction="row" spacing={1.5} alignItems="center">
                        <Box
                          sx={{
                            display: 'inline-flex',
                            alignItems: 'center',
                            justifyContent: 'center',
                            width: 44,
                            height: 44,
                            borderRadius: '14px',
                            bgcolor: 'primary.lighter',
                            color: 'primary.main',
                            flexShrink: 0,
                          }}
                        >
                          {itemIcon}
                        </Box>
                        <Box>
                          <Typography variant="h6" fontWeight={700}>
                            {getGearLabel(item.gearType)}
                          </Typography>
                          <Typography variant="body2" color="text.secondary">
                            {item.inventory.organizationName}
                          </Typography>
                        </Box>
                      </Stack>

                      <Typography variant="body1">{item.details || t('gearDetail.noDetails', 'No additional details')}</Typography>

                      <Stack direction="row" spacing={1} flexWrap="wrap" useFlexGap>
                        <Chip size="small" label={item.inventory.city} />
                        <Chip size="small" variant="outlined" label={formatCurrency(item.pricePerDay, item.currencyCode) + ' / ' + t('gearDetail.perDay', 'day')} />
                        <Chip size="small" variant="outlined" label={item.inventory.organizationName} />
                      </Stack>

                      <Divider />

                      <Box>
                        <Typography variant="subtitle2" color="text.secondary" gutterBottom>
                          {t('gearDetail.pickupAddress', 'Pickup address')}
                        </Typography>
                        <Typography>{formatInventoryAddress(item)}</Typography>
                      </Box>

                      <Alert severity="info">{t('gearDetail.returnByNotice', 'Return by {{time}} on the return date.', { time: returnByTime })}</Alert>
                    </Stack>
                  </CardContent>
                </Card>
              </Grid>

              <Grid item xs={12} md={5}>
                <Paper variant="outlined" sx={{ p: 2.5, borderRadius: 3 }}>
                  <Stack spacing={2}>
                    <Typography variant="h6" fontWeight={700}>
                      {t('gearDetail.reservationTitle', 'Reserve gear')}
                    </Typography>

                    <LocalizationProvider dateAdapter={AdapterDayjs}>
                      <Stack spacing={2}>
                        <DatePicker
                          label={t('gearDetail.startDate', 'Start date')}
                          value={startDate}
                          onChange={(value) => setStartDate(value)}
                          slotProps={{ textField: { fullWidth: true, size: 'small' } }}
                        />
                        <DatePicker
                          label={t('gearDetail.endDate', 'End date')}
                          value={endDate}
                          onChange={(value) => setEndDate(value)}
                          minDate={startDate ?? undefined}
                          slotProps={{ textField: { fullWidth: true, size: 'small' } }}
                        />
                      </Stack>
                    </LocalizationProvider>

                    <TextField
                      label={t('gearDetail.quantity', 'Quantity')}
                      type="number"
                      value={quantity}
                      onChange={(event) => setQuantity(Math.max(1, Number.parseInt(event.target.value || '1', 10) || 1))}
                      inputProps={{ min: 1, step: 1 }}
                      fullWidth
                      size="small"
                    />

                    {availabilityLoading ? (
                      <Stack direction="row" spacing={1} alignItems="center">
                        <CircularProgress size={18} />
                        <Typography variant="body2" color="text.secondary">
                          {t('gearDetail.checkingAvailability', 'Checking availability...')}
                        </Typography>
                      </Stack>
                    ) : availabilityError ? (
                      <Alert severity="warning">{availabilityError}</Alert>
                    ) : availability === true ? (
                      <Alert severity="success">{t('gearDetail.available', 'Available for selected dates')}</Alert>
                    ) : availability === false ? (
                      <Alert severity="error">{t('gearDetail.unavailable', 'Not available for selected dates')}</Alert>
                    ) : null}

                    {selectedDateRangeValid && rentalDays > 0 ? (
                      <Paper variant="outlined" sx={{ p: 2, borderRadius: 2, bgcolor: 'background.default' }}>
                        <Typography variant="body2" color="text.secondary">
                          {t('gearDetail.referenceTotalLabel', 'Reference total price')}
                        </Typography>
                        <Typography variant="h6" fontWeight={700}>
                          {formatCurrency(referenceTotal, item.currencyCode)}
                        </Typography>
                        <Typography variant="caption" color="text.secondary">
                          {t('gearDetail.referenceTotalHint', '{{quantity}} × {{price}} × {{days}} days', {
                            quantity,
                            price: formatCurrency(item.pricePerDay, item.currencyCode),
                            days: rentalDays,
                          })}
                        </Typography>
                      </Paper>
                    ) : null}

                    {!selectedDateRangeValid && (startDate || endDate) ? (
                      <Alert severity="warning">{t('gearDetail.invalidDateRange', 'End date must be after start date')}</Alert>
                    ) : null}

                    <Button variant="contained" size="large" disabled={!canReserve} onClick={handleReserve}>
                      {t('gearDetail.reserve', 'Reserve')}
                    </Button>
                  </Stack>
                </Paper>
              </Grid>
            </Grid>
          ) : null}
        </Stack>
      </Container>

      <Dialog open={confirmOpen} onClose={() => setConfirmOpen(false)} maxWidth="sm" fullWidth>
        <DialogTitle>{t('gearDetail.confirmDialogTitle', 'Confirm reservation')}</DialogTitle>
        <DialogContent>
          <Stack spacing={2} sx={{ pt: 1 }}>
            <Typography>{t('gearDetail.confirmDialogBody', 'Please review the reservation before submitting it.')}</Typography>
            <Divider />
            <Stack spacing={1}>
              <Typography variant="body2" color="text.secondary">
                {t('gearDetail.dateRangeLabel', 'Dates')}
              </Typography>
              <Typography>{selectedDateRange}</Typography>
              <Typography variant="body2" color="text.secondary">
                {t('gearDetail.quantity', 'Quantity')}
              </Typography>
              <Typography>{quantity}</Typography>
              <Typography variant="body2" color="text.secondary">
                {t('gearDetail.referenceTotalLabel', 'Reference total price')}
              </Typography>
              <Typography>{item ? formatCurrency(referenceTotal, item.currencyCode) : ''}</Typography>
              <Typography variant="body2" color="text.secondary">
                {t('gearDetail.pickupAddress', 'Pickup address')}
              </Typography>
              <Typography>{item ? formatInventoryAddress(item) : ''}</Typography>
            </Stack>
          </Stack>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setConfirmOpen(false)}>{t('common.cancel', 'Cancel')}</Button>
          <Button onClick={() => void handleSubmitReservation()} variant="contained" disabled={submitLoading}>
            {submitLoading ? t('common.loading', 'Loading...') : t('common.confirm', 'Confirm')}
          </Button>
        </DialogActions>
      </Dialog>
    </>
  );
}